/**
 * Stream state machine for dual-stream SSE.
 *
 * State graph:
 *   IDLE -> THINKING_ACTIVE -> THINKING_DONE -> SESSION_REVALIDATING -> FINAL_ACTIVE -> FINAL_DONE -> STREAM_CLOSED
 *   Error paths: THINKING_ACTIVE | SESSION_REVALIDATING | FINAL_ACTIVE -> ERROR -> STREAM_CLOSED
 *
 * Rule: Final response MUST NOT appear while thinking steps show "running".
 * Defensive: first final token while THINKING_ACTIVE forces THINKING_DONE (inferred).
 */

export type StreamState =
  | 'IDLE'
  | 'THINKING_ACTIVE'
  | 'THINKING_DONE'
  | 'SESSION_REVALIDATING'
  | 'FINAL_ACTIVE'
  | 'FINAL_DONE'
  | 'STREAM_CLOSED'
  | 'ERROR';

export interface StreamStateContext {
  streamState: StreamState;
  hasSeenThinkingDone: boolean;
  hasSeenFinalStart: boolean;
  hasSeenFinalDone: boolean;
  inferredThinkingCompletion: boolean;
  errorMessage?: string;
}

const ILLEGAL_TRANSITIONS: Array<{ from: StreamState; to: StreamState }> = [
  { from: 'THINKING_ACTIVE', to: 'STREAM_CLOSED' },
  { from: 'FINAL_ACTIVE', to: 'THINKING_ACTIVE' },
  { from: 'FINAL_DONE', to: 'THINKING_ACTIVE' },
  { from: 'STREAM_CLOSED', to: 'THINKING_ACTIVE' },
  { from: 'STREAM_CLOSED', to: 'FINAL_ACTIVE' },
  { from: 'STREAM_CLOSED', to: 'THINKING_ACTIVE' },
];

function logIllegalTransition(from: StreamState, to: StreamState): void {
  if (ILLEGAL_TRANSITIONS.some((t) => t.from === from && t.to === to)) {
    console.warn('[streamState] illegal_transition_detected', { from, to });
  }
}

function logTransition(event: string, from: StreamState, to: StreamState, inferred?: boolean): void {
  if (typeof window !== 'undefined' && (window as unknown as { __MARCH_DEBUG_STREAM__?: boolean }).__MARCH_DEBUG_STREAM__) {
    console.debug('[streamState]', event, { from, to, inferred });
  }
}

export const INITIAL: StreamStateContext = {
  streamState: 'IDLE',
  hasSeenThinkingDone: false,
  hasSeenFinalStart: false,
  hasSeenFinalDone: false,
  inferredThinkingCompletion: false,
};

export type StreamAction =
  | { type: 'THINKING_EVENT'; payload: { event: string; done?: boolean } }
  | { type: 'THINKING_COMPLETE' }
  | { type: 'FIRST_FINAL_TOKEN' }
  | { type: 'FINAL_DONE' }
  | { type: 'ERROR'; payload: { message: string } }
  | { type: 'RESET' };

/**
 * Reducer for stream state. Enforces edge rules.
 * Returns [newContext, shouldMarkAllThinkingComplete].
 * When shouldMarkAllThinkingComplete is true, caller must set all thinking steps to completed.
 */
export function streamStateReducer(
  ctx: StreamStateContext,
  action: StreamAction
): [StreamStateContext, boolean] {
  const prev = ctx.streamState;
  let next = ctx;
  let markComplete = false;

  switch (action.type) {
    case 'RESET':
      return [INITIAL, false];

    case 'THINKING_EVENT': {
      const { event, done } = action.payload;
      if (prev === 'IDLE') {
        next = { ...ctx, streamState: 'THINKING_ACTIVE' };
        logTransition('thinking_started', prev, next.streamState);
      }
      if (event === 'thinking.complete' || done === true) {
        if (prev === 'THINKING_ACTIVE') {
          next = {
            ...ctx,
            streamState: 'THINKING_DONE',
            hasSeenThinkingDone: true,
          };
          markComplete = true;
          logTransition('thinking_completed', prev, next.streamState);
        }
      }
      return [next, markComplete];
    }

    case 'THINKING_COMPLETE': {
      if (prev === 'THINKING_ACTIVE') {
        next = {
          ...ctx,
          streamState: 'THINKING_DONE',
          hasSeenThinkingDone: true,
        };
        markComplete = true;
        logTransition('thinking_completed', prev, next.streamState);
      }
      return [next, markComplete];
    }

    case 'FIRST_FINAL_TOKEN': {
      if (prev === 'THINKING_ACTIVE') {
        logTransition('thinking_completed_inferred', prev, 'THINKING_DONE', true);
        next = {
          ...ctx,
          streamState: 'FINAL_ACTIVE',
          hasSeenThinkingDone: true,
          hasSeenFinalStart: true,
          inferredThinkingCompletion: true,
        };
        markComplete = true;
        logTransition('final_started', 'THINKING_DONE', next.streamState);
      } else if (prev === 'THINKING_DONE' || prev === 'SESSION_REVALIDATING') {
        next = {
          ...ctx,
          streamState: 'FINAL_ACTIVE',
          hasSeenFinalStart: true,
        };
        logTransition('final_started', prev, next.streamState);
      } else if (prev === 'IDLE') {
        next = {
          ...ctx,
          streamState: 'FINAL_ACTIVE',
          hasSeenThinkingDone: true,
          hasSeenFinalStart: true,
          inferredThinkingCompletion: true,
        };
        markComplete = true;
        logTransition('final_started', prev, next.streamState);
      } else {
        logIllegalTransition(prev, 'FINAL_ACTIVE');
      }
      return [next, markComplete];
    }

    case 'FINAL_DONE': {
      if (prev === 'FINAL_ACTIVE') {
        next = {
          ...ctx,
          streamState: 'STREAM_CLOSED',
          hasSeenFinalDone: true,
        };
        logTransition('final_completed', prev, next.streamState);
      } else if (prev !== 'STREAM_CLOSED' && prev !== 'ERROR') {
        next = {
          ...ctx,
          streamState: 'STREAM_CLOSED',
          hasSeenFinalDone: true,
        };
        logTransition('stream_closed', prev, next.streamState);
      }
      return [next, false];
    }

    case 'ERROR': {
      if (prev !== 'STREAM_CLOSED' && prev !== 'ERROR') {
        next = {
          ...ctx,
          streamState: 'STREAM_CLOSED',
          errorMessage: action.payload.message,
        };
        logTransition('error', prev, next.streamState);
        markComplete = prev === 'THINKING_ACTIVE';
      }
      return [next, markComplete];
    }

    default:
      return [ctx, false];
  }
}

/**
 * Mark all running thinking events as completed.
 */
export function markAllThinkingStepsComplete<T extends { status: string }>(
  events: T[]
): T[] {
  return events.map((e) =>
    e.status === 'running' ? { ...e, status: 'success' as const } : e
  );
}
